import { useCallback } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';

const keys = queryKeys.messages;

export function useConversationsQuery(params = {}) {
  return useQuery({
    queryKey: keys.conversations(params),
    queryFn: () => portalApi.messages(params),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000
  });
}

export function useThreadQuery(contactNumber) {
  return useQuery({
    queryKey: keys.thread(contactNumber),
    queryFn: () => portalApi.messageThread(contactNumber),
    enabled: Boolean(contactNumber),
    refetchInterval: contactNumber ? 10_000 : false
  });
}

export function useSuggestionQuery(contactNumber, lastId, enabled) {
  return useQuery({
    queryKey: keys.suggestion(contactNumber, lastId),
    queryFn: () => portalApi.suggestReply(contactNumber),
    enabled: Boolean(contactNumber) && Boolean(enabled),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false
  });
}

export function useContactContextQuery(contactNumber, enabled = true) {
  return useQuery({
    queryKey: queryKeys.contacts.detail(contactNumber),
    queryFn: () => portalApi.contactContext(contactNumber).then((r) => r.context),
    enabled: Boolean(contactNumber) && enabled,
    staleTime: 5 * 60_000
  });
}

const patchConversation = (contactNumber, patch) => (data) =>
  data?.conversations
    ? {
        ...data,
        conversations: data.conversations.map((c) => (c.contactNumber === contactNumber ? { ...c, ...patch } : c))
      }
    : data;

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useCallback(
    (contactNumber) => qc.setQueriesData({ queryKey: keys.conversationLists() }, patchConversation(contactNumber, { unread: 0 })),
    [qc]
  );
}

export function useSendMessage() {
  return useApiMutation({
    mutationFn: ({ contactNumber, body }) => portalApi.sendMessage(contactNumber, body),
    optimistic: ({ contactNumber, body }) => {
      const now = new Date().toISOString();
      const pending = {
        _id: `temp-${Date.now()}`,
        direction: 'outbound',
        body,
        createdAt: now,
        status: 'queued',
        _optimistic: true
      };
      return [
        {
          key: keys.thread(contactNumber),
          update: (data) => ({ ...data, messages: [...(data.messages || []), pending] })
        },
        {
          key: keys.conversationLists(),
          update: patchConversation(contactNumber, {
            lastMessage: body,
            lastDirection: 'outbound',
            lastAt: now,
            unread: 0,
            needsAttention: false
          })
        }
      ];
    },
    invalidate: [keys.all],
    error: 'Message not sent'
  });
}

export function useResolveConversation() {
  return useApiMutation({
    mutationFn: (contactNumber) => portalApi.resolveConversation(contactNumber),
    optimistic: (contactNumber) => [
      { key: keys.conversationLists(), update: patchConversation(contactNumber, { needsAttention: false }) }
    ],
    invalidate: [keys.conversationLists()],
    success: 'Conversation resolved',
    error: 'Could not resolve this conversation'
  });
}

export function useAddContactNote(contactNumber) {
  return useApiMutation({
    mutationFn: ({ clientId, notes }) => portalApi.updateClient(clientId, { notes }),
    optimistic: ({ notes }) => [
      { key: queryKeys.contacts.detail(contactNumber), update: (ctx) => ({ ...ctx, notes }) }
    ],
    invalidate: [queryKeys.contacts.detail(contactNumber)],
    success: 'Note added',
    error: 'Could not save this note'
  });
}
